import { FC, useState } from "react";
import { useRouter } from "expo-router";
import { toast } from "sonner-native";

import {
  ChildCaregiverStatusEnumType,
  TaxFilingStatusEnumType,
} from "@libs/api/patients";
import { EligibilityScreenLayout } from "@/blocks/EligibilityScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateHouseholdMutation } from "@/data/mutations/householdsMutations";
import { useListHouseholdMemberRelationshipsByPrimaryMemberQuery } from "@/data/queries/householdMemberRelationshipsQueries";
import { useCurrentHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";
import { useGetHouseholdQuery } from "@/data/queries/householdsQueries";
import { useTranslator } from "@/hooks/useTranslate";
import { getSomeoneSupportsYouOptions } from "@/utils/eligibility";

import { EligibilityAddHouseholdMemberScreen } from "./EligibilityAddHouseholdMemberScreen";

type YesButtonPropsType = {
  option: string;
  isLoading?: boolean;
  onPress: () => void;
};

const YesButton: FC<YesButtonPropsType> = ({ option, isLoading, onPress }) => (
  <VnButton
    isLoading={isLoading}
    onPress={onPress}
    variant="outline"
    className="justify-start py-7 md:py-1">
    <VnText variant="lg">{option}</VnText>
  </VnButton>
);

export const EligibilitySomeoneSupportsYouScreen = () => {
  const router = useRouter();

  const { t } = useTranslator();

  const [buttonPressedId, setButtonPressedId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [isAddingNewHouseholdMember, setIsAddingNewHouseholdMember] =
    useState(false);

  const { data: currentHouseholdMember } = useCurrentHouseholdMemberQuery();

  const { data: householdMemberRelationships } =
    useListHouseholdMemberRelationshipsByPrimaryMemberQuery({
      isEnabled: true,
    });

  const { mutateAsync: updateHouseholdMember } =
    useUpdateHouseholdMemberMutation();
  const { mutateAsync: updateHousehold, isPending: isUpdatingHousehold } =
    useUpdateHouseholdMutation();
  const { data: household } = useGetHouseholdQuery();

  const hasFinishedEligibilityFlow =
    household?.eligibilityLastStep === "FINISHED";

  if (!currentHouseholdMember?.id) {
    return <VnLoadingSpinner />;
  }

  const handleButtonPress = async (buttonId: number) => {
    setButtonPressedId(buttonId);

    const hasPressedNoButton = buttonId === someoneSupportsYouOptions.length;

    // Get the primary's spouse or common law from relationships
    const spouseOrCommonLawHouseholdMemberRelationship =
      householdMemberRelationships?.find(hhmRelationship =>
        ["SPOUSE", "PARTNER"].includes(hhmRelationship.relationship),
      );

    let taxFilingStatusIfNoSpouseOrCommonLaw: TaxFilingStatusEnumType | null =
      null;
    let childCaregiverStatus: ChildCaregiverStatusEnumType | null = null;
    const currentHouseholdMemberChildCaregiverStatus =
      currentHouseholdMember.childCaregiverStatus ?? null;

    switch (true) {
      // If there's no spouse or common law and pressed no, then taxFillingStatus is Single
      case !spouseOrCommonLawHouseholdMemberRelationship && hasPressedNoButton:
        taxFilingStatusIfNoSpouseOrCommonLaw = "SINGLE";
        childCaregiverStatus = currentHouseholdMemberChildCaregiverStatus;
        break;
      // If there's no spouse or common law and pressed any yes option, then taxFillingStatus is non-filer
      case !spouseOrCommonLawHouseholdMemberRelationship && !hasPressedNoButton:
        taxFilingStatusIfNoSpouseOrCommonLaw = "NON_FILER";
        childCaregiverStatus = currentHouseholdMemberChildCaregiverStatus;
        break;
      // If pressed no button, taxFilingStatus is Jointly
      case hasPressedNoButton:
        taxFilingStatusIfNoSpouseOrCommonLaw = "JOINTLY";
        childCaregiverStatus = currentHouseholdMemberChildCaregiverStatus;
        break;
      // If pressed any yes option, fallback to Separately
      default:
        taxFilingStatusIfNoSpouseOrCommonLaw = "SEPARATELY";
        childCaregiverStatus = "NOT_CAREGIVER";
        break;
    }

    const updatePromises = [
      updateHouseholdMember({
        id: currentHouseholdMember.id,
        householdMemberData: {
          taxFilingStatus: taxFilingStatusIfNoSpouseOrCommonLaw,
          childCaregiverStatus,
        },
      }),
    ];

    if (spouseOrCommonLawHouseholdMemberRelationship) {
      updatePromises.push(
        updateHouseholdMember({
          id: spouseOrCommonLawHouseholdMemberRelationship.relationshipHouseholdMemberId,
          householdMemberData: {
            taxFilingStatus: hasPressedNoButton ? "JOINTLY" : "SEPARATELY",
            childCaregiverStatus,
            householdMemberRelationship: {
              taxRelationship: hasPressedNoButton
                ? "FILES_JOINTLY_WITH"
                : "DEPENDENT_OF_SUPPORTER",
            },
          },
        }),
      );
    }

    try {
      setIsLoading(true);
      await Promise.all(updatePromises);

      if (hasPressedNoButton) {
        await updateHousehold(
          {
            id: currentHouseholdMember.householdId,
            householdData: {
              eligibilityLastStep: hasFinishedEligibilityFlow
                ? "FINISHED"
                : "SOMEONE_SUPPORTS_YOU",
            },
          },
          {
            onSuccess: () => {
              router.navigate("/eligibility/household-income");
            },
          },
        );
        return;
      }

      setIsAddingNewHouseholdMember(true);
    } catch {
      toast.error(
        t("native:eligibilitySomeoneSupportsYou.errors.updateFailed"),
      );
    } finally {
      setIsLoading(false);
      setButtonPressedId(null);
    }
  };

  if (isAddingNewHouseholdMember) {
    return (
      <EligibilityAddHouseholdMemberScreen
        relationshipOptions={[
          "PARENT",
          "GRANDPARENT",
          "SIBLING",
          "CHILD",
          "STEPCHILD",
          "GRANDCHILD",
          "FOSTER_CHILD",
          "OTHER",
        ]}
        assumptions={{
          householdMemberRelationship: {
            livingWithStatus: true,
            taxRelationship: "DEPENDENT_OF",
          },
          sameMailingAddress: true,
        }}
        titleI18nKey="native:eligibilitySomeoneSupportsYou.formTitle"
        descriptionI18nKey="native:eligibilitySomeoneSupportsYou.formDescription"
        onSuccessOrContinue={addedHouseholdMember => {
          if (addedHouseholdMember) {
            void updateHousehold(
              {
                id: currentHouseholdMember.householdId,
                householdData: {
                  eligibilityLastStep: hasFinishedEligibilityFlow
                    ? "FINISHED"
                    : "SOMEONE_SUPPORTS_YOU",
                },
              },
              {
                onSuccess: () => {
                  router.navigate({
                    pathname: "/eligibility/marital-status",
                    params: {
                      supporterFirstName: addedHouseholdMember.firstName,
                      supporterHouseholdMemberId: addedHouseholdMember.id,
                    },
                  });
                },
              },
            );
          }
        }}
        onBackButtonPress={() => setIsAddingNewHouseholdMember(false)}
      />
    );
  }

  const someoneSupportsYouOptions = getSomeoneSupportsYouOptions();

  return (
    <EligibilityScreenLayout
      shouldShowBackButton={false}
      titleI18nKey="native:eligibilitySomeoneSupportsYou.title"
      descriptionI18nKey="native:eligibilitySomeoneSupportsYou.description">
      <VnBox className="gap-3">
        {someoneSupportsYouOptions.map((option, index) => (
          <YesButton
            key={option}
            option={option}
            onPress={() => {
              void handleButtonPress(index);
            }}
            isLoading={
              buttonPressedId === index && (isLoading || isUpdatingHousehold)
            }
          />
        ))}
        <VnButton
          onPress={() => {
            void handleButtonPress(someoneSupportsYouOptions.length);
          }}
          variant="outline"
          className="justify-start"
          i18nKey="common:no"
          isLoading={
            buttonPressedId === someoneSupportsYouOptions.length &&
            (isLoading || isUpdatingHousehold)
          }
        />
      </VnBox>
    </EligibilityScreenLayout>
  );
};
